import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import i18n from 'src/i18n'
import { atomDossier, atomQuestionnaire } from 'src/storage'
import { ProductsCodeMap } from 'src/ui/pages/products/products_mapping.utils'
import { array, boolean, object, string } from 'yup'

export const useValidationSchemas = () => {
  const { t } = useTranslation()
  const questionnaire = useRecoilValue(atomQuestionnaire)
  const dossier = useRecoilValue(atomDossier)

  const step1 = object({
    warranties: array().test('non-null', t('commons.required'), (warranties) => {
      if (dossier.codProd === ProductsCodeMap.RC01) {
        return warranties?.[0]?.isSelected !== null && warranties?.[0]?.isSelected !== undefined
      }

      return warranties?.some((warranty) => warranty?.isSelected)
    }),
  })

  const step2 = object().shape({
    questions: array()
      .of(
        object()
          .shape({
            value: string().required('Seleziona una risposta'),
          })
          .required()
      )
      .test('check-for-null', t('commons.required'), (questions) => {
        if (!questions || questions.length !== questionnaire?.questionario?.domande.length) {
          return false
        }
        for (let i = 0; i < questions?.length; i++) {
          if (questions?.[i]?.value === null || questions?.[i]?.value === undefined) {
            return false
          }
        }
        return true
      })
      .required('Le domande sono obbligatorie')
      .length(questionnaire?.questionario?.domande.length),
  })

  const step3 = object({
    consentDigitalTransmission: boolean()
      .oneOf([true], t('commons.required'))
      .required(t('commons.required')),
    consentInformativeSet: boolean()
      .oneOf([true], t('commons.required'))
      .required(t('commons.required')),
    consentPrivacy: boolean().oneOf([true], t('commons.required')).required(t('commons.required')),
    email: string()
      .email(i18n.t('commons.invalid_email'))
      .required(i18n.t('commons.email_required')),
    phone: string().matches(regexPhone, i18n.t('commons.invalid_phone')),
  })

  const step4 = object({
    paymentMethod: string().required(t('commons.required')),
    iban: string()
      .required(t('commons.required'))
      .test('check-iban', t('commons.required'), (ibanValue) => {
        if (!ibanValue) return false

        return validateIban(ibanValue)
      }),
    bankProduct: string().required(t('commons.required')),
    paymentType: string().required(t('commons.required')),
    consentPayment: boolean().oneOf([true], t('commons.required')).required(t('commons.required')),
  })

  return [step1, step2, step3, step4]
}

export const regexEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/gm

export const regexPhone = /^(\+\d{1,3}[-\s]?)?\d{3}[-\s]?\d{6,7}$/gm

export const regexIban = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/

export const cleanIban = (iban: string) => iban.replace(/\s+/g, '').toUpperCase()

const validateIban = (iban: string) => {
  // Clean iban value
  const ibanValue = cleanIban(iban)

  // Check format base
  const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/
  if (!ibanRegex.test(ibanValue)) {
    return false
  }

  // Check digit (mod 97)
  const rearranged = ibanValue.slice(4) + ibanValue.slice(0, 4)
  const converted = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55))

  let remainder = ''
  for (const digit of converted) {
    remainder = (Number(remainder + digit) % 97).toString()
  }

  return remainder === '1'
}
